import AddIcon from '@atlaskit/icon/core/add';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import Popup, { REDUCED_POPUP_OFFSET } from '@databases/atlaskit/Popup';
import { contextualMenuState } from '@databases/contextual-menu/contextual-menu-state';
import { useIsAiSummaryFieldEnabled } from '@databases/database-ai-fields';
import { runAiSummaryField } from '@databases/database-ai-fields/run-ai-summary-field';
import AddFieldMenu, {
  type AddFieldMenuSelection
} from '@databases/field-type/AddFieldMenu';
import FieldTypeDropdown, {
  type FieldTypeTriggerProps
} from '@databases/field-type/FieldTypeDropdown';
import { getFieldType, FieldTypeCategory } from '@databases/fields';
import {
  DatabaseTraceAction,
  DatabaseTraceSource,
  traceDatabaseAction
} from '@databases/frontend-metrics/perf-tracing';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { useComponent } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { getService } from '@databases/service-container';
import { SkeletonItem } from '@databases/skeleton';
import { type AiSummaryFieldConfig, DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { createField, getExistingFieldNames } from '@databases/utils/database/utils';
import { getUniqueFieldName } from '@databases/utils/field-name';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, {
  type ComponentType,
  type FunctionComponent,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
// eslint-disable-next-line no-restricted-imports -- Databases is provided React Intl v5 context.
import { useIntl } from 'react-intl-next';
// eslint-disable-next-line no-restricted-imports -- Existing table layout depends on this exported styled selector.
import styled from 'styled-components';
import { subscribe } from 'valtio';

import { useIsChangeboardingActive } from '@canvas/changeboarding';
import { getExperimentValue } from '@canvas/client-feature-flags';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import { useDatabaseAiConfigModal } from '../ai-config/database-ai-config-modal-context.ts';

export const AddFieldButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  > * {
    width: 100%;
    flex-grow: 1;
  }
`;

const addIconContainerStyles = xcss({
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: '0',
  boxSizing: 'border-box',
  borderRadius: 'radius.small',
  position: 'relative'
});

const addFieldSpotlightDotPositionStyles = xcss({
  position: 'absolute',
  top: 'space.0',
  right: 'space.0',
  width: '6px',
  height: '6px',
  pointerEvents: 'none'
});

const addFieldSpotlightDotStyles = xcss({
  width: '6px',
  height: '6px',
  borderRadius: 'radius.full'
});

const addIconContainerOpenStyles = xcss({
  borderWidth: 'border.width',
  borderStyle: 'solid',
  color: 'color.text.selected',
  backgroundColor: 'color.background.selected',
  borderColor: 'color.border.selected'
});

interface AddFieldButtonProps {
  contentId: string;
  currentViewId: string;
  triggerWrapper?: ComponentType;
  isDisabled?: boolean;

  showTypes?: DatabaseFieldType[];
  ensureSingleSelect?: boolean;
  customTrigger?: (props: FieldTypeTriggerProps) => ReactElement;
  placement?: 'top-start' | 'bottom-end' | 'bottom-start';
  width?: number;
  onFieldSelected?: (fieldType: DatabaseFieldType) => void;
}

/**
 * Renders a button that will add a field to a database. The database update is drafted automatically on click.
 */
const AddFieldButton: FunctionComponent<AddFieldButtonProps> = ({
  contentId,
  currentViewId,
  isDisabled,
  showTypes,
  ensureSingleSelect = false,
  customTrigger,
  placement = 'bottom-end',
  width,
  onFieldSelected
}) => {
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const { showPanel } = usePanel();
  const { openAiConfigModal } = useDatabaseAiConfigModal();
  const client = useAnalytics();
  const isAiFieldsSummaryEnabled = useIsAiSummaryFieldEnabled();
  const isAiSummaryChangeboardingActive = useIsChangeboardingActive('dbAiSummary');
  const shouldShowAddFieldSpotlightDot =
    isAiFieldsSummaryEnabled && isAiSummaryChangeboardingActive && !isDisabled;
  const isFieldContextualMenusEnabled = getExperimentValue(
    'databasesContextualMenusM2',
    'enabled',
    {
      fireExperimentExposure: false
    }
  );

  const { formatMessage, locale } = useIntl();
  const addFieldLabel = formatMessage(messages['table-layout.add-field-label']);
  const [isAddFieldMenuOpen, setIsAddFieldMenuOpen] = useState(false);
  const isAddFieldMenuOpenRef = useRef(isAddFieldMenuOpen);
  const fieldPickerSessionIdRef = useRef<string | null>(null);

  const database = selectDatabaseByContentIdRequired(store.getState(), {
    contentId
  });

  const handleClick = useCallback(
    (type: DatabaseFieldType) => {
      const reliability = ReliabilityMetrics.getInstance();

      try {
        let createdField: ReturnType<typeof createField> | undefined;

        traceDatabaseAction(
          { action: DatabaseTraceAction.FieldAdd, source: DatabaseTraceSource.Table },
          () => {
            selectionsActions.deselectAll();

            const field = createField(type);
            if (ensureSingleSelect) {
              field.settings.multi = false;
            }

            /**
             * Keep the AI Summary type label distinct from its default field name;
             * the generic path would append "field" to the type label.
             */
            const fieldLabel =
              type === DatabaseFieldType.AI_SUMMARY
                ? formatMessage(getFieldType(field.type).defaultText)
                : formatMessage(
                    messages['field-settings.field-name-inline-edit-default-text'],
                    {
                      defaultFieldName: formatMessage(getFieldType(field.type).label)
                    }
                  );
            const uniqueFieldName = getUniqueFieldName(
              fieldLabel,
              getExistingFieldNames(database, field)
            );
            createdField = { ...field, name: uniqueFieldName };

            dispatch(
              addChangeToDraft(contentId, {
                type: DatabaseChangeType.ADD_FIELD,
                field: createdField,
                currentViewId
              })
            );
          }
        );
        client.sendCreateDatabaseFieldEvent({
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
          source: 'databaseCreateFieldDialog',
          fieldType: type
        });
        client.sendDatabaseFieldOptionUpdatedEvent({
          source: 'database',
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
          fieldSelection: type,
          isNewField: true
        });

        // Call the optional callback after field creation (for context-specific analytics)
        onFieldSelected?.(type);

        reliability.success(MetricName.DATABASE_ADD_FIELD);

        if (getFieldType(type).category !== FieldTypeCategory.BASIC && createdField) {
          manuallyFireExposureEvent('databasesContextualMenusM2');
          if (!isFieldContextualMenusEnabled) {
            showPanel('FieldCreatePanel', {
              type,
              field: createdField
            });
          }
        }

        return createdField;
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        reliability.failure(MetricName.DATABASE_ADD_FIELD, {
          errorMessage
        });
        throw e;
      }
    },
    [
      client,
      contentId,
      onFieldSelected,
      isFieldContextualMenusEnabled,
      selectionsActions,
      ensureSingleSelect,
      formatMessage,
      database,
      dispatch,
      currentViewId,
      showPanel
    ]
  );

  const pressableStyles = xcss({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'space.0',
    color: 'color.text',
    backgroundColor: 'elevation.surface.sunken',
    ':active': {
      backgroundColor: 'elevation.surface.hovered'
    }
  });

  const visualRefreshBorderStyles = xcss({
    borderWidth: 'border.width',
    borderStyle: 'solid',
    borderColor: 'color.border',
    borderTopLeftRadius: '0',
    borderTopRightRadius: 'radius.xlarge',
    borderBottomRightRadius: '0',
    borderBottomLeftRadius: '0'
  });

  const pressableStylesClosed = xcss({
    ':hover': {
      backgroundColor: 'elevation.surface.hovered'
    }
  });

  const pressableStylesDisabled = xcss({
    color: 'color.text.subtlest',
    ':hover': {
      color: 'color.text.subtlest',
      backgroundColor: 'elevation.surface'
    },
    ':active': {
      color: 'color.text.subtlest',
      backgroundColor: 'elevation.surface'
    }
  });

  const trigger = useComponent(
    // The `Pressable` component does not support an `isSelected` prpo. It gets passed through to the underlying
    // `<button>` DOM element, which results in a React warning
    ({ ref, isSelected: _isSelected, ...props }: FieldTypeTriggerProps) => {
      const isOpen = props['aria-expanded'] === true;

      return (
        <SkeletonItem isButton height={20} width={20}>
          <Tooltip content={addFieldLabel} position="top" hideTooltipOnClick>
            {/* @ts-ignore - wrong types coming from Atlaskit */}
            <Pressable
              testId="add-field-button"
              id="add-field-button"
              role="button"
              xcss={[
                pressableStyles,
                !isOpen && pressableStylesClosed,
                isDisabled && pressableStylesDisabled,
                visualRefreshBorderStyles
              ]}
              ref={ref}
              isDisabled={isDisabled}
              {...props}
            >
              <Box
                testId="add-field-button-icon-container"
                xcss={[addIconContainerStyles, isOpen && addIconContainerOpenStyles]}
              >
                <AddIcon label={addFieldLabel} />
                {shouldShowAddFieldSpotlightDot && (
                  <Box
                    xcss={addFieldSpotlightDotPositionStyles}
                    testId="ai-summary-add-field-spotlight-dot"
                    aria-hidden="true"
                  >
                    <SpotlightPulse
                      bgColor={token('color.border.discovery')}
                      radius={3}
                      testId="ai-summary-add-field-spotlight-pulse"
                    >
                      <Box xcss={addFieldSpotlightDotStyles} />
                    </SpotlightPulse>
                  </Box>
                )}
              </Box>
            </Pressable>
          </Tooltip>
        </SkeletonItem>
      );
    },
    [
      addFieldLabel,
      isDisabled,
      pressableStyles,
      pressableStylesClosed,
      pressableStylesDisabled,
      shouldShowAddFieldSpotlightDot,
      visualRefreshBorderStyles
    ]
  );

  const handleAddFieldMenuOpenChange = useCallback(() => {
    if (!isDisabled) {
      if (!isAddFieldMenuOpen) {
        manuallyFireExposureEvent('databasesContextualMenusM2');
        if (isAiFieldsSummaryEnabled) {
          manuallyFireExposureEvent('databasesFrontendAiFieldsSummary');
        }
      }
      setIsAddFieldMenuOpen((isOpen) => {
        if (!isOpen) {
          fieldPickerSessionIdRef.current = globalThis.crypto.randomUUID();
        }
        return !isOpen;
      });
    }
  }, [isAddFieldMenuOpen, isAiFieldsSummaryEnabled, isDisabled]);

  useEffect(() => {
    isAddFieldMenuOpenRef.current = isAddFieldMenuOpen;
  }, [isAddFieldMenuOpen]);

  useEffect(
    () =>
      subscribe(contextualMenuState, () => {
        if (isAddFieldMenuOpenRef.current && contextualMenuState.isAnyMenuOpen()) {
          setIsAddFieldMenuOpen(false);
        }
      }),
    []
  );

  const persistAiSummaryConfig = useCallback(
    (fieldId: string, config: AiSummaryFieldConfig) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_AI_FIELD_CONFIG,
          fieldId,
          aiFieldConfig: {
            aiFieldType: 'summary',
            field: config
          }
        })
      );
    },
    [contentId, dispatch]
  );

  const handleAddFieldMenuSelect = useCallback(
    (selection: AddFieldMenuSelection) => {
      setIsAddFieldMenuOpen(false);
      const createdField = handleClick(selection.fieldType);

      if (isAiFieldsSummaryEnabled && selection.type === 'ai-summarize' && createdField) {
        void client.sendAiFieldTypeSelectedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          fieldPickerSessionId:
            fieldPickerSessionIdRef.current ?? globalThis.crypto.randomUUID(),
          aiFieldType: 'summary'
        });
        openAiConfigModal({
          type: 'ai-summary-field',
          props: {
            fieldId: createdField.id,
            fieldName: createdField.name,
            mode: 'add',
            openedFrom: 'addFieldContextualMenu',
            onSaveAndRun: (config, abortSignal) => {
              persistAiSummaryConfig(createdField.id, config);
              return runAiSummaryField({
                abortSignal,
                contentId,
                fieldId: createdField.id,
                targetFieldName: createdField.name,
                config,
                locale,
                viewId: currentViewId,
                operation: 'initial-generation',
                triggerType: 'saveAndRun',
                runScope: 'entireColumn',
                source: 'aiFieldModal'
              });
            },
            onSaveWithoutRunning: (config) =>
              persistAiSummaryConfig(createdField.id, config)
          }
        });
      }
    },
    [
      contentId,
      currentViewId,
      client,
      handleClick,
      isAiFieldsSummaryEnabled,
      locale,
      openAiConfigModal,
      persistAiSummaryConfig
    ]
  );

  const handleAiSummaryChangeboardingShown = useCallback(() => {
    void client.sendAiSummaryChangeboardingShownEvent({
      contentId,
      databaseContainer: getDatabaseContainerType()
    });
  }, [client, contentId]);

  const addFieldMenuTrigger = useComponent(
    ({ ref, ...props }) =>
      (customTrigger || trigger)({
        ref,
        ...props,
        onClick: handleAddFieldMenuOpenChange,
        isSelected: props['aria-expanded']
      }),
    [customTrigger, handleAddFieldMenuOpenChange, trigger]
  );

  const fieldTypeDropdownTrigger = useComponent(
    ({ onClick, ...props }: FieldTypeTriggerProps) =>
      (customTrigger || trigger)({
        ...props,
        onClick: (event) => {
          if (!isDisabled) {
            manuallyFireExposureEvent('databasesContextualMenusM2');
            manuallyFireExposureEvent('databasesFrontendAiFieldsSummary');
          }
          onClick?.(event);
        }
      }),
    [customTrigger, isDisabled, trigger]
  );

  return (
    <>
      {/* For visual refresh we are adding a border radius to table header row. Due to the way table header row is being rendered, we are able to apply border radius but
      we run into an overflow issue on scroll where the the underlying table cell borders remain visible and extend beyond the rounded corners during horizontal scrolling
      Usually an overflow: hidden will solve this problem, but because we have an extra th element at the beginning and end of the row with a position of sticky this causes unwanted issues
      To resolve, we use clip-path to hide the extended table rows for now and we can revisit if we refactor our table layout
      */}
      <>
        <div
          style={{
            width: '8px',
            height: '8px',
            backgroundColor: token('elevation.surface', '#FFFFFF'),
            position: 'absolute',
            // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
            top: '-1px',
            // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
            left: '-7px',
            right: token('space.negative.075', '-6px'),
            clipPath: `path("M 0 0 L 0 16 L 26 19 C 28 0 8 18 0 0 Z")`,
            transform: 'rotate(166deg)'
          }}
        />
        <div
          style={{
            width: '8px',
            height: '8px',
            backgroundColor: token('elevation.surface', '#FFFFFF'),
            position: 'absolute',
            bottom: '0',
            left: token('space.negative.075', '-6px'),
            clipPath: `path("M 8 0 L 8 8 L 0 8 C 1 7, 7 7, 0 8 Z")`
          }}
        />
      </>
      <AddFieldButtonContainer>
        {isFieldContextualMenusEnabled ? (
          <Popup
            isOpen={isAddFieldMenuOpen && !isDisabled}
            onClose={() => setIsAddFieldMenuOpen(false)}
            shouldUseCaptureOnOutsideClick
            trigger={addFieldMenuTrigger}
            contentContainer={null}
            content={() => (
              <AddFieldMenu
                onSelect={handleAddFieldMenuSelect}
                onAiSummaryChangeboardingShown={handleAiSummaryChangeboardingShown}
                showTypes={showTypes}
              />
            )}
            placement={placement}
            offset={REDUCED_POPUP_OFFSET}
          />
        ) : (
          <FieldTypeDropdown
            trigger={fieldTypeDropdownTrigger}
            onChange={handleClick}
            placement={placement}
            offset={REDUCED_POPUP_OFFSET}
            showTypes={showTypes}
            width={width}
          />
        )}
      </AddFieldButtonContainer>
    </>
  );
};

export default AddFieldButton;
